// Pure helpers for the component payments step. The allowed payment dates come
// from the template's deadline rules; the backend validates the same windows.

import { PaymentType } from "../../types/types";
import { DeadlineRule } from "../../pages/deals/createDeal/dealFormTypes";

export type PaymentOffsets = {
  prePayment: number;
  postPayment: number;
  resultUpdated: number;
  resultConfirmed: number;
};

export type PaymentWindows = {
  preMin: string;
  preMax: string;
  postMin: string;
  postMax: string;
};

export type PaymentDates = { pre: string; post: string };

export type PaymentDateErrors = { pre: string; post: string };

type Translate = (
  key: string,
  params?: Record<string, string | number>,
) => string;

const DAY_MS = 86_400_000;

const findOffset = (rules: DeadlineRule[], stage: string): number | null => {
  const rule = rules.find((item) => item.stage === stage);
  return typeof rule?.offsetDays === "number" ? rule.offsetDays : null;
};

// Reads the four offsets the payment windows need. Returns null when the
// template does not define all of them; the backend then applies its own
// defaults and the form shows no window.
export const getPaymentOffsets = (
  rules: DeadlineRule[],
): PaymentOffsets | null => {
  const prePayment = findOffset(rules, "PRE_PAYMENT");
  const postPayment = findOffset(rules, "POST_PAYMENT");
  const resultUpdated = findOffset(rules, "RESULT_UPDATED");
  const resultConfirmed = findOffset(rules, "RESULT_CONFIRMED");

  if (
    prePayment === null ||
    postPayment === null ||
    resultUpdated === null ||
    resultConfirmed === null
  ) {
    return null;
  }

  return { prePayment, postPayment, resultUpdated, resultConfirmed };
};

// Reads "yyyy-MM-dd" (also with a time part) or the old "dd-MM-yyyy" format
// as UTC midnight. Returns null for anything else.
const parseDateToUtcMs = (value?: string | null): number | null => {
  if (!value) return null;

  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (iso) return Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));

  const legacy = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value);
  if (legacy) {
    return Date.UTC(Number(legacy[3]), Number(legacy[2]) - 1, Number(legacy[1]));
  }

  return null;
};

const toIsoDate = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

// Builds the allowed date range for the pre-event and the post-event payment.
// The pre-event payment may start no earlier than today. Returns null when
// the event date or the offsets are missing.
export const buildPaymentWindows = (
  eventDate: string | null | undefined,
  offsets: PaymentOffsets | null,
  todayIso: string,
): PaymentWindows | null => {
  const eventMs = parseDateToUtcMs(eventDate);
  if (eventMs === null || !offsets) return null;

  const earliestPre = toIsoDate(eventMs - offsets.prePayment * DAY_MS);

  return {
    preMin: earliestPre < todayIso ? todayIso : earliestPre,
    preMax: toIsoDate(eventMs),
    postMin: toIsoDate(
      eventMs + (offsets.resultUpdated + offsets.resultConfirmed) * DAY_MS,
    ),
    postMax: toIsoDate(eventMs + offsets.postPayment * DAY_MS),
  };
};

// The dates the form starts with: pay as early as allowed before the event and
// as late as allowed after it. Empty when there is no window.
export const getDefaultPaymentDates = (
  windows: PaymentWindows | null,
): PaymentDates =>
  windows
    ? { pre: windows.preMin, post: windows.postMax }
    : { pre: "", post: "" };

const isOutside = (date: string, min: string, max: string): boolean =>
  date < min || date > max;

// Validates the chosen dates against the windows. ISO dates compare as text.
export const getPaymentDateErrors = (
  params: {
    windows: PaymentWindows | null;
    dates: PaymentDates;
    paymentType: PaymentType;
    hasPrePayouts: boolean;
    hasPostPayouts: boolean;
  },
  t: Translate,
): PaymentDateErrors => {
  const { windows, dates, paymentType, hasPrePayouts, hasPostPayouts } = params;
  const isSplit = paymentType === PaymentType.SPLIT;

  let pre = "";
  if (hasPrePayouts && !dates.pre) {
    pre = t("preMatchDueDateRequired");
  } else if (
    windows &&
    dates.pre &&
    isOutside(dates.pre, windows.preMin, windows.preMax)
  ) {
    pre = t("preMatchDateBetween", {
      min: windows.preMin,
      max: windows.preMax,
    });
  }

  let post = "";
  if (isSplit && hasPostPayouts && !dates.post) {
    post = t("postMatchDueDateRequired");
  } else if (
    isSplit &&
    windows &&
    dates.post &&
    isOutside(dates.post, windows.postMin, windows.postMax)
  ) {
    post = t("postMatchDateBetween", {
      min: windows.postMin,
      max: windows.postMax,
    });
  }

  return { pre, post };
};
